import { useEffect, useRef, useState, type UIEvent } from 'react';
import clsx from 'clsx';
import { twMerge } from 'tailwind-merge';
import ArrowButton from '../ArrowButton/ArrowButton';
import type { HorizontalScrollViewProps } from './HorizontalScrollView.types';

export default function DesktopHorizontalScrollView(props: HorizontalScrollViewProps) {
  const { className, overlayClassName, step = 64, children, ref } = props;

  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);

  function updateCanScrollStates(element: HTMLDivElement | null) {
    if (!element) {
      return;
    }

    setCanScrollLeft(element.scrollLeft > 0);
    setCanScrollRight(element.scrollLeft + element.clientWidth < element.scrollWidth - 1);
  }

  useEffect(() => {
    const containerElement = containerRef.current;

    if (!containerElement) {
      return;
    }

    updateCanScrollStates(containerElement);
    const resizeObserver = new ResizeObserver(() => updateCanScrollStates(containerElement));
    resizeObserver.observe(containerElement);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  function handleRef(newRef: HTMLDivElement | null) {
    containerRef.current = newRef;

    if (typeof ref === 'function') {
      ref(newRef);
      return;
    }

    if (ref) {
      ref.current = newRef;
    }
  }

  function handleScroll(event: UIEvent<HTMLDivElement>) {
    const element = event.currentTarget;
    updateCanScrollStates(element);
  }

  function handleScrollClick(direction = 1) {
    const containerElement = containerRef.current;

    if (!containerElement) {
      return;
    }

    containerElement.scrollBy({ left: step * direction, behavior: 'smooth' });
  }

  const defaultButtonClassName =
    'absolute opacity-0 transition-opacity duration-150 pointer-events-none';
  const hoverButtonClassName = 'group-hover:pointer-events-auto group-hover:opacity-100';

  return (
    <div className="relative group">
      <div
        ref={handleRef}
        className={twMerge(
          'overflow-y-hidden overflow-x-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]',
          className,
        )}
        onScroll={handleScroll}
      >
        {children}
      </div>
      <div
        className={twMerge(
          'absolute inset-0 flex items-center justify-between pointer-events-none',
          overlayClassName,
        )}
      >
        <ArrowButton
          className={clsx(
            '-scale-x-100 left-0',
            defaultButtonClassName,
            canScrollLeft && hoverButtonClassName,
          )}
          onClick={() => handleScrollClick(-1)}
        />
        <ArrowButton
          className={clsx(
            'right-0',
            defaultButtonClassName,
            canScrollRight && hoverButtonClassName,
          )}
          onClick={() => handleScrollClick()}
        />
      </div>
    </div>
  );
}
